/**
 * 与后端 Result 报文对齐的统一解析工具。
 * 标准结构：{ code, msg, data }
 */

export const API_SUCCESS_CODE = "0000"

/** 与后端 ResultCode / 各模块 ErrorCode 对齐的常用错误码 */
export const ApiResultCode = {
  SUCCESS: "0000",
  // 认证与权限（ResultCode 14xx）
  UNAUTHORIZED: "1401",
  TOKEN_EXPIRED: "1402",
  FORBIDDEN: "1403",
  // 通用请求（ResultCode 20xx / 24xx / 99xx）
  PARAM_ERROR: "2001",
  NOT_FOUND: "2404",
  BAD_REQUEST: "2400",
  SYSTEM_ERROR: "9999",
  // 验证码与密码重置（VerificationErrorCode 15xx）
  VERIFICATION_RANGE_START: "1500",
  VERIFICATION_RANGE_END: "1599",
  // 通用业务（CommonErrorCode 21xx）
  COMMON_RANGE_START: "2100",
  COMMON_RANGE_END: "2199",
  // 门户基础数据（PortalErrorCode 22xx）
  PORTAL_RANGE_START: "2200",
  PORTAL_RANGE_END: "2299",
  // 资产模块（AssetErrorCode 31xx）
  ASSET_RANGE_START: "3100",
  ASSET_RANGE_END: "3199",
  ASSET_NOT_FOUND: "3101",
  ASSET_TYPE_REQUIRED: "3102",
  ASSET_NAME_REQUIRED: "3103",
  ASSET_NO_DUPLICATE: "3104",
  ASSET_EXPIRE_DATE_REQUIRED: "3105",
  ASSET_DETAIL_TOO_LONG: "3106",
  // CRM 模块（CrmErrorCode 32xx）
  CRM_RANGE_START: "3200",
  CRM_RANGE_END: "3299",
  // SRM 模块（SrmErrorCode 33xx）
  SRM_RANGE_START: "3300",
  SRM_RANGE_END: "3399",
  // 海外办事处资产（OverseasAssetErrorCode 34xx）
  OVERSEAS_RANGE_START: "3400",
  OVERSEAS_RANGE_END: "3499",
  // 回收站（RecycleBinErrorCode 35xx）
  RECYCLE_RANGE_START: "3500",
  RECYCLE_RANGE_END: "3599"
}

export class ApiBusinessError extends Error {
  constructor(message, code, payload, response) {
    super(message || "操作失败")
    this.name = "ApiBusinessError"
    this.code = code || ""
    this.payload = payload || null
    this.response = response || null
  }
}

const LEGACY_MESSAGE_ZH = {
  "This business document is already in approval": "当前业务已经存在审批中的申请，请勿重复提交",
  "Workflow request body is required": "审批请求参数不能为空",
  "Approval task not found": "审批任务不存在",
  "Current user is not the task approver": "当前用户不是该任务的审批人",
  "Approval task is not pending": "该审批任务已处理，不能重复操作",
  "Workflow instance not found": "审批流程实例不存在",
  "Invalid designer json": "审批流程设计数据格式不正确"
}

function localizeApiMessage(message, fallback) {
  const text = message == null ? "" : String(message).trim()
  if (!text) return fallback
  if (LEGACY_MESSAGE_ZH[text]) return LEGACY_MESSAGE_ZH[text]
  const noFlowPrefix = "No enabled approval flow found for business type:"
  if (text.startsWith(noFlowPrefix)) {
    return "当前业务类型未配置已启用的审批流：" + text.slice(noFlowPrefix.length).trim()
  }
  return text
}

export function getApiPayload(response) {
  return response && response.data ? response.data : null
}

export function isApiSuccess(payload) {
  return !!(payload && payload.code === API_SUCCESS_CODE)
}

export function getApiMessage(payload, fallback = "操作失败") {
  if (!payload) return fallback
  if (payload.msg) return localizeApiMessage(payload.msg, fallback)
  return fallback
}

export function getApiCode(payload) {
  return payload && payload.code ? String(payload.code) : ""
}

export function getApiData(payload) {
  return payload ? payload.data : null
}

/**
 * 解析 axios 响应；业务失败时抛出 ApiBusinessError。
 */
export function parseApiResponse(response, fallback = "操作失败") {
  const payload = getApiPayload(response)
  if (!isApiSuccess(payload)) {
    throw new ApiBusinessError(
      getApiMessage(payload, fallback),
      getApiCode(payload),
      payload,
      response
    )
  }
  return getApiData(payload)
}

/**
 * 仅校验成功，不返回 data。
 */
export function assertApiSuccess(response, fallback = "操作失败") {
  parseApiResponse(response, fallback);
}

/**
 * 统一解析后端错误信息（业务失败 / HTTP 异常 / 网络异常）。
 */
export function parseApiError(error, fallback = "操作失败") {
  if (!error) return fallback
  if (error instanceof ApiBusinessError) {
    return localizeApiMessage(error.message, fallback)
  }
  if (error.payload) {
    return getApiMessage(error.payload, fallback)
  }
  const payload = error.response && error.response.data
  if (payload) {
    return getApiMessage(payload, fallback)
  }
  if (error.message) return localizeApiMessage(error.message, fallback)
  return fallback
}

export function getApiErrorCode(error) {
  if (!error) return ""
  if (error instanceof ApiBusinessError) return error.code || ""
  if (error.payload) return getApiCode(error.payload)
  const payload = error.response && error.response.data
  return getApiCode(payload)
}

/**
 * 列表接口兼容：data 可能是数组，也可能是 { records, total }。
 */
export function normalizePageResult(data) {
  if (Array.isArray(data)) {
    return { records: data, total: data.length }
  }
  if (data && Array.isArray(data.records)) {
    return {
      records: data.records,
      total: Number(data.total || 0)
    }
  }
  return { records: [], total: 0 }
}
